import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { ELEVES_ACCOUNT_KEY } from '@teetsh/app/src/queries/eleves/useEleves';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import formatISO from '@teetsh/app/src/utils/time/formatISO';
import serializeDateToServer from '@teetsh/app/src/utils/time/serializeDateToServer';
import { CLASSROOMS_KEY } from '@teetsh/app/src/queries/classrooms/useClassrooms';
import { Gender } from '@teetsh/app/src/types/gender';
import {
  DEFAULT_CYCLE,
  ScholarSystem,
  ScholarSystems,
} from 'containers/scholarSystem';
import { useScholarSystem } from 'queries/user/useScholarSystem';

interface EleveMapping {
  id: string;
  givenName: string;
  familyName: string;
  birthday?: string;
  ine?: string;
  gender?: Gender;
}

interface ClassroomMapping {
  id: string;
  idBe?: string;
  name?: string;
  cycle: string;
  degree: string;
  eleves: EleveMapping[];
}

function serialize(
  classroomMapping: ClassroomMappingResult,
  scholarSystem: ScholarSystem,
  tz: string,
): ClassroomMapping {
  const cycleFromDegree =
    scholarSystem.niveaux
      .filter(n => n.niveau === classroomMapping.degree)
      .map(n => n.cycle)[0] || DEFAULT_CYCLE;
  return {
    id: classroomMapping.id,
    idBe: classroomMapping.idBe,
    name: classroomMapping.name,
    cycle: cycleFromDegree,
    degree: classroomMapping.degree,
    eleves: classroomMapping.eleves.map(ev => ({
      id: ev.id,
      givenName: ev.givenName,
      familyName: ev.familyName,
      ine: ev.ine,
      gender: ev.gender,
      customFields: ev.customFields,
      birthday: ev.birthday
        ? formatISO(serializeDateToServer(ev.birthday, tz))
        : ev.birthday,
    })),
  };
}

interface EleveMappingResult {
  id: string;
  givenName: string;
  familyName: string;
  birthday?: Date;
  ine?: string;
  gender?: Gender;
  customFields?: { [key: string]: string };
}

interface ClassroomMappingResult {
  id: string;
  idBe?: string;
  name?: string;
  cycle: string[];
  degree: string;
  eleves: EleveMappingResult[];
}

export interface ClassroomsMappingSubmit {
  classrooms: ClassroomMappingResult[];
}

export default function useImportEleves() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const user = useUser();
  const queryClient = useQueryClient();
  const { scholarSystem = ScholarSystems.FR } = useScholarSystem();

  const {
    mutateAsync,
    isPending: isLoading,
    isSuccess,
  } = useMutation({
    mutationFn: (mapping: ClassroomsMappingSubmit) =>
      request(
        `/schoolyears/${schoolyear.id}/schools/${school.id}/eleves/import`,
        {
          method: 'POST',
          body: mapping.classrooms.map(cl =>
            serialize(cl, scholarSystem, user.location),
          ),
        },
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ELEVES_ACCOUNT_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CLASSROOMS_KEY],
      });
    },
  });
  return {
    importEleves: mutateAsync,
    isImporting: isLoading,
    isSuccess,
  };
}
